import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Popover} from '@dropbox/dig-components/popover';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {TextInput, type TextInputRefObject} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {OpenLine, SearchLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {
  KNOWLEDGE_SOURCES,
  knowledgeLinkState,
  readKnowledgeSource,
} from 'analytics/knowledgeAnalytics';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {Breadcrumb, BreadcrumbLinkProps} from 'components/DSYS/Breadcrumb';
import {normalizeHtmlListOrder} from 'components/DSYS/editor/utils/htmlLists';
import {
  isLexicalJsonString,
  parseAndExtractTextFromLexicalJson,
} from 'components/DSYS/editor/utils/lexical';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {Title} from 'components/DSYS/Title';
import {getKnowledgeCategoryPath, getKnowledgePagePath, ROUTE_PATHS} from 'constant';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {NotFound} from 'pages/NotFound';
import {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router';
import newsPostStyles from 'views/news/NewsPostRight.module.css';

import {
  logServiceNowKnowledgePageView,
  ServiceNowKnowledgePageNode,
  ServiceNowNavCategory,
  ServiceNowNavMenuNode,
} from './api';
import {useKnowledgeContent, useKnowledgeNavMenu} from './hooks';
import {matchesKnowledgeSearch} from './knowledgeSearch';
import styles from './KnowledgeView.module.css';

export const flattenTree = (nodes: ServiceNowKnowledgePageNode[]): ServiceNowKnowledgePageNode[] =>
  nodes.flatMap((node) => [node, ...flattenTree(node.children)]);

const getExternalHref = (node: ServiceNowKnowledgePageNode): string | null => {
  const candidate = node.url?.trim();
  if (!candidate) {
    return null;
  }
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
};

type ExternalNodeKind = 'link' | 'request';
const LINK_NODE_PREFIX = 'link-';
const ARTICLE_NODE_PREFIX = 'article-';

export const getExternalNodeKind = (node: ServiceNowKnowledgePageNode): ExternalNodeKind | null => {
  if (!getExternalHref(node)) {
    return null;
  }
  if (node.node_type === 'link' || node.node_type === 'request') {
    return node.node_type;
  }
  if (node.id.startsWith(LINK_NODE_PREFIX)) {
    return 'link';
  }
  if (node.id.startsWith('request-')) {
    return 'request';
  }
  return null;
};

const isExternalNode = (node: ServiceNowKnowledgePageNode): boolean =>
  getExternalNodeKind(node) !== null;

const isTopicNode = (node: ServiceNowKnowledgePageNode): boolean =>
  node.node_type === 'topic' || node.is_container;

const isUrlLikeTitle = (value?: string | null): boolean =>
  /^https?:\/\//i.test((value || '').trim());

const normalizeExternalTitle = (value?: string | null): string =>
  (value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const externalNodeSort = (
  a: ServiceNowKnowledgePageNode,
  b: ServiceNowKnowledgePageNode
): number => {
  if (a.sort_order !== b.sort_order) {
    return a.sort_order - b.sort_order;
  }
  return a.title.localeCompare(b.title);
};

const preferExternalNodeTitle = (
  first: ServiceNowKnowledgePageNode,
  second: ServiceNowKnowledgePageNode
): ServiceNowKnowledgePageNode => {
  const firstIsUrlTitle = isUrlLikeTitle(first.title);
  const secondIsUrlTitle = isUrlLikeTitle(second.title);

  if (firstIsUrlTitle !== secondIsUrlTitle) {
    return firstIsUrlTitle ? second : first;
  }

  const firstTitleLength = (first.title || '').trim().length;
  const secondTitleLength = (second.title || '').trim().length;
  if (firstTitleLength !== secondTitleLength) {
    return firstTitleLength > secondTitleLength ? first : second;
  }

  return externalNodeSort(first, second) <= 0 ? first : second;
};

const dedupeExternalNodes = (
  nodes: ServiceNowKnowledgePageNode[],
  {
    dedupeByTitle = false,
    dropUrlLikeTitles = false,
  }: {dedupeByTitle?: boolean; dropUrlLikeTitles?: boolean} = {}
): ServiceNowKnowledgePageNode[] => {
  const byUrlOrFallbackKey = new Map<string, ServiceNowKnowledgePageNode>();

  nodes.forEach((node) => {
    if (dropUrlLikeTitles && isUrlLikeTitle(node.title)) {
      return;
    }
    const href = getExternalHref(node);
    const key = href
      ? `url:${href.toLowerCase()}`
      : `title:${(node.title || '').trim().toLowerCase()}`;
    const existing = byUrlOrFallbackKey.get(key);
    if (!existing) {
      byUrlOrFallbackKey.set(key, node);
      return;
    }
    byUrlOrFallbackKey.set(key, preferExternalNodeTitle(existing, node));
  });

  let deduped = Array.from(byUrlOrFallbackKey.values());

  if (dedupeByTitle) {
    const byTitleKey = new Map<string, ServiceNowKnowledgePageNode>();
    deduped.forEach((node) => {
      const titleKey = normalizeExternalTitle(node.title);
      if (!titleKey) {
        return;
      }
      const existing = byTitleKey.get(titleKey);
      if (!existing) {
        byTitleKey.set(titleKey, node);
        return;
      }
      byTitleKey.set(titleKey, preferExternalNodeTitle(existing, node));
    });
    deduped = Array.from(byTitleKey.values());
  }

  return deduped.sort(externalNodeSort);
};

const stripHtmlToText = (value: string): string => {
  if (!value.includes('<')) {
    return value;
  }

  if (typeof document !== 'undefined') {
    const container = document.createElement('div');
    container.innerHTML = value;
    const text = container.textContent ?? container.innerText ?? '';
    if (text.trim()) {
      return text;
    }
  }

  return value.replace(/<[^>]*>?/g, ' ');
};

const previewText = (body?: string | null): string => {
  if (!body) {
    return '';
  }

  const extracted = isLexicalJsonString(body)
    ? parseAndExtractTextFromLexicalJson(body)
    : stripHtmlToText(body);

  return extracted.replace(/\s+/g, ' ').trim();
};

const knowledgePagePreviewCache = new WeakMap<ServiceNowKnowledgePageNode, string>();

export const getCachedPagePreviewText = (page: ServiceNowKnowledgePageNode): string => {
  const cached = knowledgePagePreviewCache.get(page);
  if (cached !== undefined) {
    return cached;
  }

  const preview = previewText(page.body);
  knowledgePagePreviewCache.set(page, preview);
  return preview;
};

const formatUpdatedDate = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const collectArticleDescendants = (
  nodes: ServiceNowKnowledgePageNode[]
): ServiceNowKnowledgePageNode[] => {
  const seenById = new Map<string, ServiceNowKnowledgePageNode>();
  const queue = [...nodes];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }
    if (current.node_type === 'article' && !seenById.has(current.id)) {
      seenById.set(current.id, current);
    }
    current.children.forEach((child) => {
      queue.push(child);
    });
  }

  return Array.from(seenById.values()).sort((a, b) => {
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }
    return a.title.localeCompare(b.title);
  });
};

const partitionKnowledgeNodes = (
  nodes: ServiceNowKnowledgePageNode[],
  {
    collectDescendantArticles = false,
    collectDescendantQuickLinks = false,
    collectDescendantRequests = false,
  }: {
    collectDescendantArticles?: boolean;
    collectDescendantQuickLinks?: boolean;
    collectDescendantRequests?: boolean;
  } = {}
): {
  topics: ServiceNowKnowledgePageNode[];
  supportResources: ServiceNowKnowledgePageNode[];
  requests: ServiceNowKnowledgePageNode[];
  quickLinks: ServiceNowKnowledgePageNode[];
} => {
  const descendantDepth = collectDescendantQuickLinks || collectDescendantRequests ? 1 : 0;
  const nodesByKind = nodes.map((node) => ({node, kind: getExternalNodeKind(node)}));
  const quickLinks = nodesByKind
    .filter(
      (entry): entry is {node: ServiceNowKnowledgePageNode; kind: 'link'} => entry.kind === 'link'
    )
    .map((entry) => entry.node);
  const requests = nodesByKind
    .filter(
      (entry): entry is {node: ServiceNowKnowledgePageNode; kind: 'request'} =>
        entry.kind === 'request'
    )
    .map((entry) => entry.node);
  const remainingNodes = nodesByKind
    .filter((entry) => entry.kind === null)
    .map((entry) => entry.node);
  const topics = remainingNodes.filter(isTopicNode);
  const supportResources = collectDescendantArticles
    ? collectArticleDescendants(remainingNodes)
    : remainingNodes.filter((node) => !isTopicNode(node));

  if (!collectDescendantQuickLinks && !collectDescendantRequests) {
    return {topics, supportResources, requests, quickLinks};
  }

  const seenQuickLinkIds = new Set(quickLinks.map((node) => node.id));
  const descendantQuickLinks: ServiceNowKnowledgePageNode[] = [];
  const seenRequestIds = new Set(requests.map((node) => node.id));
  const descendantRequests: ServiceNowKnowledgePageNode[] = [];
  const queue = topics.map((node) => ({node, depth: 0}));

  while (queue.length > 0) {
    const currentEntry = queue.shift();
    const current = currentEntry?.node;
    if (!current) {
      continue;
    }
    current.children.forEach((childNode) => {
      const childKind = getExternalNodeKind(childNode);

      if (
        collectDescendantQuickLinks &&
        childKind === 'link' &&
        !seenQuickLinkIds.has(childNode.id)
      ) {
        seenQuickLinkIds.add(childNode.id);
        descendantQuickLinks.push(childNode);
      }
      if (
        collectDescendantRequests &&
        childKind === 'request' &&
        !seenRequestIds.has(childNode.id)
      ) {
        seenRequestIds.add(childNode.id);
        descendantRequests.push(childNode);
      }
      if (isTopicNode(childNode) && (currentEntry?.depth ?? 0) < descendantDepth) {
        queue.push({node: childNode, depth: (currentEntry?.depth ?? 0) + 1});
      }
    });
  }

  return {
    topics,
    supportResources,
    requests: dedupeExternalNodes([...requests, ...descendantRequests], {
      dedupeByTitle: true,
      dropUrlLikeTitles: true,
    }),
    quickLinks: dedupeExternalNodes([...quickLinks, ...descendantQuickLinks]),
  };
};

export const SubPageCardList = ({
  pages,
  categorySlug,
  linkSource = KNOWLEDGE_SOURCES.card,
  onPageClick,
}: {
  pages: ServiceNowKnowledgePageNode[];
  categorySlug: string;
  linkSource?: string;
  onPageClick?: (page: ServiceNowKnowledgePageNode, index: number) => void;
}) => {
  if (pages.length === 0) {
    return null;
  }
  return (
    <div className={styles.subPagesGrid}>
      {pages.map((childPage, index) => {
        const childCategorySlug = childPage.primary_category_slug || categorySlug;
        const contentPreview = getCachedPagePreviewText(childPage);
        const cardLabelParts = [childPage.title];
        if (contentPreview) {
          cardLabelParts.push(contentPreview);
        }
        const cardAriaLabel = cardLabelParts.join('. ');
        const externalHref = isExternalNode(childPage) ? getExternalHref(childPage) : null;

        if (externalHref) {
          return (
            <a
              key={childPage.id}
              className={styles.subPageCard}
              href={externalHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={cardAriaLabel}
              onClick={() => onPageClick?.(childPage, index)}
            >
              <Text isBold className={styles.subPageCardTitle}>
                {childPage.title}
              </Text>
              {contentPreview ? (
                <Text color="subtle" className={styles.subPageCardPreview}>
                  {contentPreview}
                </Text>
              ) : null}
            </a>
          );
        }

        return (
          <Link
            key={childPage.id}
            className={styles.subPageCard}
            to={getKnowledgePagePath(childCategorySlug, childPage.id)}
            state={knowledgeLinkState(linkSource)}
            monochromatic
            hasNoUnderline
            aria-label={cardAriaLabel}
            onClick={() => onPageClick?.(childPage, index)}
          >
            <Text isBold className={styles.subPageCardTitle}>
              {childPage.title}
            </Text>
            {contentPreview ? (
              <Text color="subtle" className={styles.subPageCardPreview}>
                {contentPreview}
              </Text>
            ) : null}
          </Link>
        );
      })}
    </div>
  );
};

const getDisplayableQuickLinks = (links: ServiceNowKnowledgePageNode[]) =>
  links
    .map((node) => ({node, href: getExternalHref(node)}))
    .filter((entry): entry is {node: ServiceNowKnowledgePageNode; href: string} =>
      Boolean(entry.href)
    );

const QUICK_LINKS_COLLAPSED_LIMIT = 5;
const CONTENT_SECTION_COLLAPSED_LIMIT = 12;
export const KNOWLEDGE_SEARCH_MAX_LENGTH = 200;

const useScopedKnowledgeSearch = (scopeKey: string): [string, (nextQuery: string) => void] => {
  const [searchState, setSearchState] = useState({scopeKey, query: ''});
  const searchQuery = searchState.scopeKey === scopeKey ? searchState.query : '';
  const setSearchQuery = useCallback(
    (nextQuery: string) => setSearchState({scopeKey, query: nextQuery}),
    [scopeKey]
  );

  return [searchQuery, setSearchQuery];
};

const KnowledgeSearchPopover = memo(function KnowledgeSearchPopover({
  controlId,
  onSearchQueryChange,
}: {
  controlId: string;
  onSearchQueryChange: (nextQuery: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<TextInputRefObject>(null);

  useLayoutEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleQueryChange = useCallback(
    (nextQuery: string) => {
      // Keep the text field update urgent and let the expensive results render yield to it.
      setQuery(nextQuery);
      startTransition(() => onSearchQueryChange(nextQuery));
    },
    [onSearchQueryChange]
  );

  const handleToggle = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        setQuery('');
        onSearchQueryChange('');
      }
    },
    [onSearchQueryChange]
  );

  return (
    <Popover
      open={isOpen}
      onToggle={handleToggle}
      placement="bottom-end"
      spacing="small"
      isPortaled={false}
      shouldFocusContentOnOpen={false}
    >
      <Popover.Trigger asChild>
        <IconButton aria-label={t('knowledge_show_search')} size="large" variant="borderless">
          <UIIcon src={SearchLine} isOpticallyAligned />
        </IconButton>
      </Popover.Trigger>
      <Popover.Overlay>
        <Popover.Content>
          <Box style={{width: 280, padding: '12px'}}>
            <TextInput
              id={`${controlId}-search`}
              ref={inputRef}
              type="search"
              size="small"
              value={query}
              maxLength={KNOWLEDGE_SEARCH_MAX_LENGTH}
              placeholder={t('knowledge_search_placeholder')}
              aria-label={t('knowledge_search_placeholder')}
              withLeftAccessory={<UIIcon src={SearchLine} role="presentation" size="small" />}
              onChange={(event) => handleQueryChange(event.currentTarget.value)}
            />
          </Box>
        </Popover.Content>
      </Popover.Overlay>
    </Popover>
  );
});

const KnowledgeSidebar = ({
  topicPages,
  displayLinks,
  categorySlug,
  resetKey,
}: {
  topicPages: ServiceNowKnowledgePageNode[];
  displayLinks: Array<{node: ServiceNowKnowledgePageNode; href: string}>;
  categorySlug: string;
  resetKey: string;
}) => {
  const navigate = useNavigate();
  const [expandedLinksKey, setExpandedLinksKey] = useState<string | null>(null);
  const showAllLinks = expandedLinksKey === resetKey;
  const visibleLinks = showAllLinks
    ? displayLinks
    : displayLinks.slice(0, QUICK_LINKS_COLLAPSED_LIMIT);

  if (topicPages.length === 0 && displayLinks.length === 0) {
    return null;
  }

  return (
    <aside className={styles.knowledgeSidebar}>
      {topicPages.length > 0 ? (
        <section className={styles.sidebarSection} aria-labelledby="knowledge-sidebar-topics">
          <Text id="knowledge-sidebar-topics" isBold>
            {t('knowledge_topics')}
          </Text>
          <div className={styles.sidebarTopicList}>
            {topicPages.map((topicPage) => (
              <Chip
                key={topicPage.id}
                className={styles.sidebarTopicChip}
                type="button"
                size="small"
                onClick={() =>
                  navigate(
                    getKnowledgePagePath(
                      topicPage.primary_category_slug || categorySlug,
                      topicPage.id
                    ),
                    {state: knowledgeLinkState(KNOWLEDGE_SOURCES.topic)}
                  )
                }
              >
                <Chip.Content>{topicPage.title}</Chip.Content>
              </Chip>
            ))}
          </div>
        </section>
      ) : null}
      {displayLinks.length > 0 ? (
        <section className={styles.sidebarSection} aria-labelledby="knowledge-sidebar-quick-links">
          <Text id="knowledge-sidebar-quick-links" isBold>
            {t('knowledge_quick_links')}
          </Text>
          <div className={styles.quickLinksList}>
            {visibleLinks.map(({node, href}) => {
              // Some quick links point at content we already host in-app (e.g. a
              // synced KB article). Route there directly instead of bouncing out
              // to ServiceNow, even though the same content may also appear
              // elsewhere (e.g. the Articles section) — that duplication is fine.
              const internalPagePath = node.internal_page_id
                ? getKnowledgePagePath(
                    node.primary_category_slug || 'content',
                    node.internal_page_id
                  )
                : null;

              return (
                <Text variant="label" size="small" key={node.id} className={styles.quickLinkRow}>
                  <Link
                    to={internalPagePath ?? href}
                    state={
                      internalPagePath ? knowledgeLinkState(KNOWLEDGE_SOURCES.quickLink) : undefined
                    }
                    className={styles.quickLink}
                    openInNewTab={!internalPagePath}
                    hasNoUnderline
                    monochromatic
                    showUnderlineOnHover
                    rel={internalPagePath ? undefined : 'noopener noreferrer'}
                    aria-label={
                      internalPagePath
                        ? node.title
                        : `${node.title}, ${t('knowledge_opens_in_new_window')}`
                    }
                  >
                    <Truncate maxWidth={246} tooltipControlProps={{placement: 'top-start'}}>
                      {node.title}
                    </Truncate>
                  </Link>
                </Text>
              );
            })}
            {displayLinks.length > QUICK_LINKS_COLLAPSED_LIMIT ? (
              <Button
                variant="transparent"
                size="small"
                onClick={() => setExpandedLinksKey(showAllLinks ? null : resetKey)}
              >
                {showAllLinks
                  ? t('show_less')
                  : t('show_more_count', {
                      count: displayLinks.length - QUICK_LINKS_COLLAPSED_LIMIT,
                    })}
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}
    </aside>
  );
};

const KnowledgeContentSections = ({
  supportResourcePages,
  requestPages,
  categorySlug,
  searchQuery,
}: {
  supportResourcePages: ServiceNowKnowledgePageNode[];
  requestPages: ServiceNowKnowledgePageNode[];
  categorySlug: string;
  searchQuery: string;
}) => {
  const [showAllArticles, setShowAllArticles] = useState(false);
  const [showAllRequests, setShowAllRequests] = useState(false);
  const normalizedSearchQuery = searchQuery.trim();
  const isSearching = normalizedSearchQuery.length > 0;
  const filteredArticles = useMemo(
    () =>
      isSearching
        ? supportResourcePages.filter((page) =>
            matchesKnowledgeSearch(normalizedSearchQuery, [
              page.title,
              getCachedPagePreviewText(page).slice(0, 1000),
            ])
          )
        : supportResourcePages,
    [isSearching, normalizedSearchQuery, supportResourcePages]
  );
  const filteredRequests = useMemo(
    () =>
      isSearching
        ? requestPages.filter((page) =>
            matchesKnowledgeSearch(normalizedSearchQuery, [
              page.title,
              getCachedPagePreviewText(page).slice(0, 1000),
            ])
          )
        : requestPages,
    [isSearching, normalizedSearchQuery, requestPages]
  );
  const visibleArticles =
    isSearching || showAllArticles
      ? filteredArticles
      : filteredArticles.slice(0, CONTENT_SECTION_COLLAPSED_LIMIT);
  const visibleRequests =
    isSearching || showAllRequests
      ? filteredRequests
      : filteredRequests.slice(0, CONTENT_SECTION_COLLAPSED_LIMIT);

  if (isSearching && filteredArticles.length === 0 && filteredRequests.length === 0) {
    return (
      <Box paddingY="24" textAlign="center" role="status">
        <Text color="subtle">
          {t('knowledge_search_no_results', {query: normalizedSearchQuery})}
        </Text>
      </Box>
    );
  }

  return (
    <>
      {filteredArticles.length > 0 ? (
        <div className={styles.sectionBlock}>
          <SubPageCardList pages={visibleArticles} categorySlug={categorySlug} />
          {!isSearching && filteredArticles.length > CONTENT_SECTION_COLLAPSED_LIMIT ? (
            <Box paddingTop="12">
              <Button
                variant="transparent"
                size="small"
                onClick={() => setShowAllArticles((current) => !current)}
              >
                {showAllArticles
                  ? t('show_less')
                  : t('show_more_count', {
                      count: filteredArticles.length - CONTENT_SECTION_COLLAPSED_LIMIT,
                    })}
              </Button>
            </Box>
          ) : null}
        </div>
      ) : null}
      {filteredRequests.length > 0 ? (
        <div className={styles.sectionBlock}>
          <Text isBold>{t('knowledge_requests')}</Text>
          <Box paddingTop="20">
            <SubPageCardList pages={visibleRequests} categorySlug={categorySlug} />
          </Box>
          {!isSearching && filteredRequests.length > CONTENT_SECTION_COLLAPSED_LIMIT ? (
            <Box paddingTop="12">
              <Button
                variant="transparent"
                size="small"
                onClick={() => setShowAllRequests((current) => !current)}
              >
                {showAllRequests
                  ? t('show_less')
                  : t('show_more_count', {
                      count: filteredRequests.length - CONTENT_SECTION_COLLAPSED_LIMIT,
                    })}
              </Button>
            </Box>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

const KnowledgeCategoryHub = ({
  category,
  rootPages,
}: {
  category: ServiceNowNavCategory;
  rootPages: ServiceNowKnowledgePageNode[];
}) => {
  const categorySlug = category.slug;
  const {
    topics: topicPages,
    supportResources: supportResourcePages,
    requests: requestPages,
    quickLinks,
  } = useMemo(
    () =>
      partitionKnowledgeNodes(rootPages, {
        collectDescendantArticles: true,
        collectDescendantRequests: true,
      }),
    [rootPages]
  );
  const displayQuickLinks = useMemo(() => getDisplayableQuickLinks(quickLinks), [quickLinks]);
  const location = useLocation();
  const viewSource = readKnowledgeSource(location.state);

  useEffect(() => {
    analyticsLogger().logEvent('KNOWLEDGE_CATEGORY_VIEWED', {
      category_slug: categorySlug,
      category_name: category.name,
      source: viewSource,
    });
    // Log once per category, with the source it was opened from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug]);

  const searchScopeKey = `category:${categorySlug}`;
  const [searchQuery, setSearchQuery] = useScopedKnowledgeSearch(searchScopeKey);
  const hasSearchableContent = supportResourcePages.length + requestPages.length > 0;
  const hasSidebarContent = topicPages.length > 0 || displayQuickLinks.length > 0;
  const layoutClassName = hasSidebarContent
    ? styles.pageContentLayout
    : `${styles.pageContentLayout} ${styles.pageContentLayoutSingleColumn}`;
  const hubBreadcrumbPath: BreadcrumbLinkProps[] = [
    {
      to: ROUTE_PATHS.KNOWLEDGE,
      state: knowledgeLinkState(KNOWLEDGE_SOURCES.breadcrumb),
      children: t('knowledge'),
    },
    {to: getKnowledgeCategoryPath(categorySlug), children: category.name},
  ];

  return (
    <div className={layoutClassName}>
      <div className={newsPostStyles.newsPostRight}>
        <div
          className={styles.breadcrumbActionsRow}
          role="navigation"
          aria-label={t('knowledge_breadcrumb_nav')}
        >
          <Breadcrumb path={hubBreadcrumbPath} withMarginBottom={false} />
        </div>
        <div className={styles.pageTitleRow}>
          <Title
            className={`${newsPostStyles.title} ${styles.pageTitle}`}
            weightVariant="emphasized"
            size="large"
          >
            {category.name}
          </Title>
          {hasSearchableContent ? (
            <KnowledgeSearchPopover
              key={searchScopeKey}
              controlId={searchScopeKey}
              onSearchQueryChange={setSearchQuery}
            />
          ) : null}
        </div>
        <KnowledgeContentSections
          key={categorySlug}
          supportResourcePages={supportResourcePages}
          requestPages={requestPages}
          categorySlug={categorySlug}
          searchQuery={searchQuery}
        />
      </div>
      <KnowledgeSidebar
        topicPages={topicPages}
        displayLinks={displayQuickLinks}
        categorySlug={categorySlug}
        resetKey={searchScopeKey}
      />
    </div>
  );
};

export const KnowledgeView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const viewSource = readKnowledgeSource(location.state);
  const {employee: currentEmployee} = useAtomValue(loggedInEmployeeAtom);
  const {id: idParam, category: routeCategorySlug} = useParams<{id?: string; category?: string}>();
  const requestedPageId = idParam?.trim() || null;
  const isLinkPageRoute = Boolean(requestedPageId?.startsWith(LINK_NODE_PREFIX));
  const selectedPageId = isLinkPageRoute ? null : requestedPageId;

  const userCacheScope = currentEmployee?.ldap;
  const navMenuQuery = useKnowledgeNavMenu({cacheScope: userCacheScope});
  const categories = useMemo(
    () => navMenuQuery.data?.categories ?? [],
    [navMenuQuery.data?.categories]
  );
  const selectedCategoryFromRoute = useMemo(() => {
    if (routeCategorySlug) {
      return categories.find((category) => category.slug === routeCategorySlug) ?? null;
    }
    return categories[0] ?? null;
  }, [categories, routeCategorySlug]);

  const explicitCategoryInUrl = Boolean(routeCategorySlug);

  const {
    treePages,
    selectedPage,
    isLoading: isKnowledgeContentLoading,
    isPageNotFound: isKnowledgePageNotFound,
  } = useKnowledgeContent({
    selectedPageId,
    categorySlug: selectedCategoryFromRoute?.slug ?? null,
    isNavMenuLoading: navMenuQuery.isLoading,
    cacheScope: userCacheScope,
  });
  // Navigation can fill in topic breadcrumbs after the article is visible,
  // without downloading the category's article bodies.
  const nodeById = useMemo(() => {
    const nodes = new Map<string, {id: string; title: string; parent_page_id?: string | null}>();
    const visitMenu = (pages: ServiceNowNavMenuNode[], parentId: string | null) => {
      for (const page of pages) {
        nodes.set(page.id, {...page, parent_page_id: parentId});
        visitMenu(page.children, page.id);
      }
    };
    for (const category of categories) {
      visitMenu(category.pages, null);
    }
    for (const node of flattenTree(treePages)) {
      nodes.set(node.id, node);
    }
    return nodes;
  }, [categories, treePages]);
  const selectedCategory = useMemo(() => {
    if (selectedPage?.primary_category_slug) {
      const byPage = categories.find(
        (category) => category.slug === selectedPage.primary_category_slug
      );
      if (byPage) {
        return byPage;
      }
    }
    return selectedCategoryFromRoute;
  }, [categories, selectedCategoryFromRoute, selectedPage?.primary_category_slug]);
  const routeCategory =
    selectedCategory?.slug ?? routeCategorySlug ?? categories[0]?.slug ?? 'content';

  useEffect(() => {
    if (!isLinkPageRoute || !routeCategory) {
      return;
    }
    navigate(getKnowledgeCategoryPath(routeCategory), {replace: true, state: location.state});
  }, [isLinkPageRoute, location.state, navigate, routeCategory]);

  const breadcrumbPath = useMemo<BreadcrumbLinkProps[]>(() => {
    if (!selectedPage) {
      return [];
    }

    const pageChain: Array<{id: string; title: string; parentId: string | null}> = [
      {
        id: selectedPage.id,
        title: selectedPage.title,
        parentId: selectedPage.parent_page_id ?? null,
      },
    ];
    const seen = new Set<string>([selectedPage.id]);
    let parentId = selectedPage.parent_page_id ?? null;

    while (parentId && !seen.has(parentId)) {
      seen.add(parentId);
      const parentNode = nodeById.get(parentId);
      if (!parentNode) {
        break;
      }
      pageChain.push({
        id: parentNode.id,
        title: parentNode.title,
        parentId: parentNode.parent_page_id ?? null,
      });
      parentId = parentNode.parent_page_id ?? null;
    }

    const breadcrumbs: BreadcrumbLinkProps[] = [
      {to: ROUTE_PATHS.KNOWLEDGE, children: t('knowledge')},
    ];
    if (selectedCategory) {
      breadcrumbs.push({
        to: getKnowledgeCategoryPath(selectedCategory.slug),
        children: selectedCategory.name,
      });
    }

    for (const chainPage of pageChain.reverse()) {
      breadcrumbs.push({
        to: getKnowledgePagePath(routeCategory, chainPage.id),
        children: chainPage.title,
      });
    }

    return breadcrumbs.map((breadcrumb) => ({
      ...breadcrumb,
      state: knowledgeLinkState(KNOWLEDGE_SOURCES.breadcrumb),
    }));
  }, [nodeById, routeCategory, selectedCategory, selectedPage]);

  useDocumentTitle(
    selectedPage?.title ??
      selectedCategory?.name ??
      (isKnowledgeContentLoading ? t('knowledge_loading') : t('home'))
  );

  useEffect(() => {
    if (!selectedPage) {
      return;
    }
    analyticsLogger().logEvent('KNOWLEDGE_PAGE_VIEWED', {
      page_id: selectedPage.id,
      page_title: selectedPage.title,
      node_type: selectedPage.node_type ?? undefined,
      category_slug: selectedCategory?.slug ?? routeCategory,
      category_name: selectedCategory?.name ?? undefined,
      source: viewSource,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPage?.id]);

  // ServiceNow only counts views it serves itself, so report the ones we serve
  // from our synced copy. Topics and quick links have no ServiceNow article.
  const lastReportedArticleIdRef = useRef<string | null>(null);
  useEffect(() => {
    const pageId = selectedPage?.id;
    if (
      !pageId ||
      selectedPage?.node_type !== 'article' ||
      !pageId.startsWith(ARTICLE_NODE_PREFIX) ||
      lastReportedArticleIdRef.current === pageId
    ) {
      return;
    }
    lastReportedArticleIdRef.current = pageId;
    logServiceNowKnowledgePageView(pageId).catch(() => {
      // View reporting is best-effort and must never disrupt reading.
    });
  }, [selectedPage?.id, selectedPage?.node_type]);

  useEffect(() => {
    if (!selectedCategory || selectedPageId || explicitCategoryInUrl || treePages.length === 0) {
      return;
    }
    const defaultPage = treePages[0];
    if (!defaultPage) {
      return;
    }
    navigate(getKnowledgePagePath(selectedCategory.slug, defaultPage.id), {
      replace: true,
      state: location.state,
    });
  }, [
    explicitCategoryInUrl,
    location.state,
    navigate,
    selectedCategory,
    selectedPageId,
    treePages,
  ]);

  useEffect(() => {
    if (!selectedPage) {
      return;
    }
    const canonicalCategorySlug = selectedPage.primary_category_slug || selectedCategory?.slug;
    if (!canonicalCategorySlug || routeCategorySlug === canonicalCategorySlug) {
      return;
    }
    navigate(getKnowledgePagePath(canonicalCategorySlug, selectedPage.id), {
      replace: true,
      state: location.state,
    });
  }, [location.state, navigate, routeCategorySlug, selectedCategory?.slug, selectedPage]);

  const showCategoryHub =
    !isKnowledgeContentLoading &&
    !selectedPage &&
    Boolean(selectedCategory) &&
    explicitCategoryInUrl;

  const isUnknownKnowledgeCategory =
    Boolean(routeCategorySlug) &&
    selectedPageId === null &&
    navMenuQuery.isFetched &&
    !navMenuQuery.isFetching &&
    categories.length > 0 &&
    !categories.some((category) => category.slug === routeCategorySlug);
  const fadeInRef = useSkeletonFadeIn(isKnowledgeContentLoading);

  return (
    <Layout.Container>
      <div ref={fadeInRef} className={styles.knowledgeBody}>
        {isUnknownKnowledgeCategory || isKnowledgePageNotFound ? (
          <NotFound />
        ) : isKnowledgeContentLoading ? (
          <KnowledgeContentLoading />
        ) : selectedPage ? (
          <KnowledgePageContent
            page={selectedPage}
            breadcrumbPath={breadcrumbPath}
            childPages={selectedPage.children ?? []}
            categorySlug={routeCategory}
          />
        ) : showCategoryHub && selectedCategory ? (
          <KnowledgeCategoryHub category={selectedCategory} rootPages={treePages} />
        ) : (
          <KnowledgeUnexpectedEmptyRedirect />
        )}
      </div>
    </Layout.Container>
  );
};

export const KnowledgeContentLoading = () => (
  <div className={newsPostStyles.newsPostRight}>
    <div className={styles.breadcrumbActionsRow}>
      <Skeleton.Text width={280} />
    </div>
    <div className={styles.loadingContent}>
      <Skeleton.Box height={32} withBorderRadius="medium" />
      <Skeleton.Box width={220} height={10} withBorderRadius="xsmall" />
      <Skeleton.Box height={180} withBorderRadius="large" />
      <Skeleton.Text />
      <Skeleton.Text />
      <Skeleton.Text />
    </div>
  </div>
);

const KnowledgePageContent = ({
  page,
  breadcrumbPath,
  childPages,
  categorySlug,
}: {
  page: ServiceNowKnowledgePageNode;
  breadcrumbPath: BreadcrumbLinkProps[];
  childPages: ServiceNowKnowledgePageNode[];
  categorySlug: string;
}) => {
  const {
    topics: topicChildPages,
    supportResources: supportResourcePages,
    requests: requestPages,
    quickLinks,
  } = useMemo(
    () =>
      partitionKnowledgeNodes(childPages, {
        collectDescendantRequests: true,
      }),
    [childPages]
  );
  const displayQuickLinks = useMemo(() => getDisplayableQuickLinks(quickLinks), [quickLinks]);
  const searchScopeKey = `page:${page.id}`;
  const [searchQuery, setSearchQuery] = useScopedKnowledgeSearch(searchScopeKey);
  const hasSearchableContent = supportResourcePages.length + requestPages.length > 0;
  const hasSidebarContent = topicChildPages.length > 0 || displayQuickLinks.length > 0;
  const pageContentLayoutClassName = hasSidebarContent
    ? styles.pageContentLayout
    : `${styles.pageContentLayout} ${styles.pageContentLayoutSingleColumn}`;
  const originalPageHref = getExternalHref(page);
  const formattedUpdatedDate = formatUpdatedDate(page.updated_on);
  const articleBody = useMemo(
    () =>
      page.body && !isLexicalJsonString(page.body) ? normalizeHtmlListOrder(page.body) : page.body,
    [page.body]
  );

  return (
    <div className={pageContentLayoutClassName}>
      <div className={newsPostStyles.newsPostRight}>
        {breadcrumbPath.length > 0 ? (
          <div
            className={styles.breadcrumbActionsRow}
            role="navigation"
            aria-label={t('knowledge_breadcrumb_nav')}
          >
            <div className={styles.breadcrumbCurrentPageActions}>
              <Breadcrumb path={breadcrumbPath} withMarginBottom={false} />
              {originalPageHref ? (
                <Tooltip title={t('knowledge_open_original_page')}>
                  <IconButton
                    aria-label={t('knowledge_open_original_page')}
                    href={originalPageHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="borderless"
                  >
                    <UIIcon src={OpenLine} isOpticallyAligned />
                  </IconButton>
                </Tooltip>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className={styles.pageTitleRow}>
          <Title
            className={`${newsPostStyles.title} ${styles.pageTitle}`}
            weightVariant="emphasized"
            size="large"
          >
            {page.title}
          </Title>
          {hasSearchableContent ? (
            <KnowledgeSearchPopover
              key={searchScopeKey}
              controlId={searchScopeKey}
              onSearchQueryChange={setSearchQuery}
            />
          ) : null}
        </div>
        {formattedUpdatedDate ? (
          <Text color="subtle" size="small" className={styles.updatedDate}>
            {t('knowledge_updated_date', {date: formattedUpdatedDate})}
          </Text>
        ) : null}
        {page.body ? (
          <RichTextArea
            value={articleBody ?? undefined}
            profile="paper"
            source={`knowledge-${page.id}`}
            importHtmlH3H4
            openExternalLinksInNewTab
            contentClassName={styles.knowledgeArticleBody}
          />
        ) : page.url && isExternalNode(page) ? (
          <Text>
            <a href={getExternalHref(page) ?? page.url} target="_blank" rel="noopener noreferrer">
              {t('view')}
            </a>
          </Text>
        ) : null}
        <KnowledgeContentSections
          key={page.id}
          supportResourcePages={supportResourcePages}
          requestPages={requestPages}
          categorySlug={categorySlug}
          searchQuery={searchQuery}
        />
      </div>
      <KnowledgeSidebar
        topicPages={topicChildPages}
        displayLinks={displayQuickLinks}
        categorySlug={categorySlug}
        resetKey={searchScopeKey}
      />
    </div>
  );
};

const KnowledgeUnexpectedEmptyRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(ROUTE_PATHS.HOME, {replace: true});
  }, [navigate]);
  return null;
};
